export type EegStartupTaskPriority = "critical" | "high" | "normal" | "low";

export type EegStartupScheduledTask = {
  id: string;
  priority: EegStartupTaskPriority;
  /** When false, task will not run until playbackControlsInteractive is marked. */
  allowBeforePlayable?: boolean;
  run: () => void;
};

type ScheduledHandle = {
  cancel: () => void;
};

const PRIORITY_TIMEOUT_MS: Record<EegStartupTaskPriority, number> = {
  critical: 0,
  high: 750,
  normal: 1800,
  low: 3500,
};

const PRIORITY_MIN_DELAY_MS: Record<EegStartupTaskPriority, number> = {
  critical: 0,
  high: 500,
  normal: 1000,
  low: 2200,
};

type PlayableGate = {
  isPlayable: () => boolean;
  subscribePlayable: (listener: () => void) => () => void;
};

let playableGate: PlayableGate | null = null;

/**
 * Wire the shared startup scheduler to EEG startup telemetry milestones.
 * Call once from app/viewer bootstrap (safe to call repeatedly).
 */
export function configureEegStartupSchedulerGate(gate: PlayableGate): void {
  playableGate = gate;
}

/**
 * Schedule post-playable (or critical) startup work with idle callback,
 * timeout fallback, and cancellation. Sprint 4C-006.
 */
export function scheduleEegStartupTask(
  task: EegStartupScheduledTask,
): ScheduledHandle {
  let cancelled = false;
  let idleHandle: number | null = null;
  let timeoutHandle: number | null = null;
  let unsubscribe: (() => void) | null = null;

  const clearTimers = () => {
    if (
      idleHandle != null &&
      typeof window !== "undefined" &&
      typeof window.cancelIdleCallback === "function"
    ) {
      window.cancelIdleCallback(idleHandle);
      idleHandle = null;
    }
    if (timeoutHandle != null && typeof window !== "undefined") {
      window.clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }
  };

  const execute = () => {
    if (cancelled) return;
    clearTimers();
    try {
      task.run();
    } catch (error) {
      if (typeof console !== "undefined") {
        console.warn(`[eegStartupScheduler] task "${task.id}" failed`, error);
      }
    }
  };

  const scheduleIdle = () => {
    if (cancelled || typeof window === "undefined") return;
    const timeoutMs = PRIORITY_TIMEOUT_MS[task.priority];
    const minDelayMs = PRIORITY_MIN_DELAY_MS[task.priority];
    if (task.priority === "critical" || timeoutMs === 0) {
      timeoutHandle = window.setTimeout(execute, minDelayMs);
      return;
    }

    timeoutHandle = window.setTimeout(() => {
      timeoutHandle = null;
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(execute, { timeout: timeoutMs });
        return;
      }
      execute();
    }, minDelayMs);
  };

  const allowBeforePlayable = task.allowBeforePlayable === true;
  const isPlayable = playableGate?.isPlayable() ?? true;

  if (allowBeforePlayable || isPlayable) {
    scheduleIdle();
  } else if (playableGate) {
    unsubscribe = playableGate.subscribePlayable(() => {
      unsubscribe?.();
      unsubscribe = null;
      scheduleIdle();
    });
  } else {
    scheduleIdle();
  }

  return {
    cancel: () => {
      cancelled = true;
      unsubscribe?.();
      unsubscribe = null;
      clearTimers();
    },
  };
}
